import React, { useEffect, useRef, useState } from 'react';
import { techIcons } from '../data/mock';
import {
  Code2, Server, Database, Cloud, Container, GitBranch, Terminal, Cpu,
  Braces, Layers, Box, Workflow, Monitor, FileCode, Settings, Cog
} from 'lucide-react';

const iconMap = {
  "Java": Code2, "Python": FileCode, "Spring Boot": Layers, "Django": Server,
  "React": Braces, "React Native": Monitor, "AWS": Cloud, "Docker": Container,
  "Kafka": Workflow, "PostgreSQL": Database, "Redis": Cpu, "Jenkins": Settings,
  "Git": GitBranch, "Linux": Terminal, "IntelliJ": Box, "VS Code": Cog
};

const TechStack = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-14 md:py-20 bg-gray-50 dark:bg-gray-900 transition-colors duration-500 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-gray-200 dark:bg-gray-700 rounded-full blur-3xl opacity-30 animate-morph-reverse" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <h2
          className={`text-4xl md:text-5xl lg:text-6xl font-extralight text-black dark:text-white mb-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          Tools & Tech Stack
        </h2>

        {/* Animated gradient underline with flowing shimmer effect */}
        <div className="relative h-1 mt-4 mb-16 overflow-hidden">
          <div
            className={`absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-400 to-gray-200 dark:from-white dark:via-gray-400 dark:to-gray-700 transition-all duration-1000 ${isVisible ? 'w-32 opacity-100' : 'w-0 opacity-0'}`}
            style={{ transitionDelay: '200ms' }}
          />
          {/* Animated shimmer overlay */}
          <div
            className={`absolute inset-0 w-32 overflow-hidden ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ transitionDelay: '700ms' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
          </div>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-6">
          {techIcons.map((tech, index) => {
            const IconComponent = iconMap[tech] || Code2;
            return (
              <div
                key={tech}
                className={`group flex flex-col items-center gap-3 p-4 rounded-lg transition-all duration-500 hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg dark:hover:shadow-2xl hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
                  }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center group-hover:bg-black dark:group-hover:bg-white transition-colors duration-300 shadow-sm">
                  <IconComponent size={24} className="text-gray-600 dark:text-gray-400 group-hover:text-white dark:group-hover:text-black transition-colors duration-300" />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-light text-center group-hover:text-black dark:group-hover:text-white transition-colors duration-300">
                  {tech}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TechStack;
